import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparePart } from '../types/SpareParts';
import { FileService } from '../services/fileService';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { useModalBackHandler } from '../hooks/useModalBackHandler';
import AnimatedCloseButton from './AnimatedCloseButton';

interface SparePartImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  sparePart: SparePart;
  isLoadingFullImage?: boolean;
  fullImageLoaded?: boolean;
  preloadedImageDimensions?: { width: number; height: number };
}

const SparePartImageModal: React.FC<SparePartImageModalProps> = ({
  isOpen,
  onClose,
  sparePart,
  isLoadingFullImage = false,
  fullImageLoaded = false,
  preloadedImageDimensions
}) => {
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [modalWidth, setModalWidth] = useState(400);
  const [modalHeight, setModalHeight] = useState(400);
  const [headerFooterHeight, setHeaderFooterHeight] = useState(120);

  // Görsel modal için scroll prevention
  useScrollPrevention(isOpen);

  // Modal geri düğmesi handler
  const handleModalClose = useCallback(() => {
    onClose();
  }, [onClose]);
  
  useModalBackHandler({ 
    isOpen, 
    onClose: handleModalClose, 
    enabled: true
  });

  // ESC tuşu ile modal'ı kapat
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onClose]);

  // Modal açıldığında modal boyutlarını ayarla
  useEffect(() => {
    if (isOpen) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isMobile = viewportWidth < 768;
      
      if (isMobile) {
        // Mobil: Sabit boyutlar
        const mobileWidth = Math.min(viewportWidth - 32, 400);
        const mobileHeight = Math.min(viewportHeight * 0.85, 600);
        
        setModalWidth(mobileWidth);
        setModalHeight(mobileHeight);
        setHeaderFooterHeight(120);
      } else {
        // Masaüstü: Eğer boyutlar önceden verilmişse kullan
        if (preloadedImageDimensions) {
          setImageDimensions(preloadedImageDimensions);
          calculateAndSetModalDimensions(
            preloadedImageDimensions.width, 
            preloadedImageDimensions.height, 
            viewportWidth, 
            viewportHeight
          );
        } else {
          // Boyutlar verilmemişse default boyutları kullan
          setModalWidth(400);
          setModalHeight(400);
          setHeaderFooterHeight(120);
        }
      }
    }
  }, [isOpen, preloadedImageDimensions?.width, preloadedImageDimensions?.height]);

  // Modal genişliği değiştiğinde header/footer yüksekliğini yeniden hesapla (sadece masaüstü)
  useEffect(() => {
    if (isOpen && modalWidth > 0) {
      const viewportWidth = window.innerWidth;
      const isMobile = viewportWidth < 768;
      
      // Mobil çözünürlüklerde yeniden boyutlandırma yapma
      if (isMobile) {
        return;
      }
      
      const newHeaderFooterHeight = calculateHeaderFooterHeight();
      if (newHeaderFooterHeight !== headerFooterHeight) {
        setHeaderFooterHeight(newHeaderFooterHeight);
        
        // Eğer görsel boyutları mevcutsa modal yüksekliğini yeniden hesapla
        if (imageDimensions.width > 0 && imageDimensions.height > 0) {
          const viewportHeight = window.innerHeight;
          const availableHeight = viewportHeight * 0.9;
          const availableImageHeight = availableHeight - newHeaderFooterHeight;
          
          const aspectRatio = imageDimensions.width / imageDimensions.height;
          let idealHeight = imageDimensions.height;
          
          if (idealHeight > availableImageHeight) {
            idealHeight = availableImageHeight;
          }
          
          const calculatedModalHeight = Math.max(
            Math.min(idealHeight + newHeaderFooterHeight + 64, availableHeight),
            300
          );
          
          setModalHeight(calculatedModalHeight);
        }
      }
    }
  }, [modalWidth, isOpen, imageDimensions, headerFooterHeight]);

  // Büyük görsel URL'sini oluştur
  const getFullImageUrl = (imageId: string | null) => {
    if (!imageId) return undefined;
    return FileService.getDownloadUrl(imageId);
  };

  // Header ve footer yüksekliğini hesapla
  const calculateHeaderFooterHeight = () => {
    // Ürün adının satır sayısını hesapla (yaklaşık)
    const productNameLength = sparePart.Ad?.length || 0;
    const charsPerLine = modalWidth > 500 ? 40 : modalWidth > 400 ? 30 : 25; // Yaklaşık karakter sayısı
    const nameLines = Math.ceil(productNameLength / charsPerLine);
    
    // Header yüksekliği: padding (32px) + title line height (28px) + code line height (20px) + extra lines
    const headerHeight = 32 + 28 + 20 + Math.max(0, (nameLines - 1) * 20);
    
    // Footer yüksekliği: eğer grup adı varsa 55px, yoksa 0px
    const footerHeight = sparePart.GrupAdi ? 55 : 0;
    
    return headerHeight + footerHeight;
  };

  // Modal boyutlarını hesapla ve ayarla
  const calculateAndSetModalDimensions = (naturalWidth: number, naturalHeight: number, viewportWidth: number, viewportHeight: number) => {
    // Modal için kullanılabilir alan (padding ve margin dahil)
    const availableWidth = viewportWidth - 32; // 16px padding her iki tarafta
    const availableHeight = viewportHeight * 0.9; // %90 viewport height
    
    // Header ve footer için ayrılan alan (dinamik hesaplama)
    const dynamicHeaderFooterHeight = calculateHeaderFooterHeight();
    const availableImageHeight = availableHeight - dynamicHeaderFooterHeight;
    
    // Görsel aspect ratio'sunu koru
    const aspectRatio = naturalWidth / naturalHeight;
    
    // İdeal genişlik hesapla
    let idealWidth = naturalWidth;
    let idealHeight = naturalHeight;
    
    // Eğer görsel çok büyükse, kullanılabilir alana sığdır
    if (naturalWidth > availableWidth || naturalHeight > availableImageHeight) {
      if (aspectRatio > 1) {
        // Yatay görsel - genişliğe göre ayarla
        idealWidth = Math.min(naturalWidth, availableWidth);
        idealHeight = idealWidth / aspectRatio;
        
        // Yükseklik hala fazlaysa, yüksekliğe göre ayarla
        if (idealHeight > availableImageHeight) {
          idealHeight = availableImageHeight;
          idealWidth = idealHeight * aspectRatio;
        }
      } else {
        // Dikey görsel - yüksekliğe göre ayarla
        idealHeight = Math.min(naturalHeight, availableImageHeight);
        idealWidth = idealHeight * aspectRatio;
        
        // Genişlik hala fazlaysa, genişliğe göre ayarla
        if (idealWidth > availableWidth) {
          idealWidth = availableWidth;
          idealHeight = idealWidth / aspectRatio;
        }
      }
    }
    
    // Modal boyutlarını hesapla (görsel + padding + border)
    const calculatedModalWidth = Math.max(
      Math.min(idealWidth + 64, availableWidth), // 32px padding her iki tarafta
      320 // Minimum genişlik
    );
    
    const calculatedModalHeight = Math.max(
      Math.min(idealHeight + dynamicHeaderFooterHeight + 64, availableHeight), // Header/footer + padding
      300 // Minimum yükseklik
    );
    
    setModalWidth(calculatedModalWidth);
    setModalHeight(calculatedModalHeight);
    setHeaderFooterHeight(dynamicHeaderFooterHeight);
  };

  // Görsel yüklendiğinde boyutları kaydet (boyutlandırma artık modal açılırken yapılıyor)
  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const { naturalWidth, naturalHeight } = img;
    
    // Sadece boyutları kaydet - boyutlandırma zaten modal açılırken yapıldı
    setImageDimensions({ width: naturalWidth, height: naturalHeight });
    
    // Eğer modal açılırken boyutlandırma yapılmadıysa (cache'li görsel durumu), yeniden hesapla
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isMobile = viewportWidth < 768;
    
    // Sadece masaüstünde ve modal boyutları default değerindeyse yeniden hesapla
    if (!isMobile && modalWidth === 400 && modalHeight === 400) {
      calculateAndSetModalDimensions(naturalWidth, naturalHeight, viewportWidth, viewportHeight);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col mx-4"
            style={{ 
              width: `${modalWidth}px`,
              height: `${modalHeight}px`,
              minWidth: '320px',
              minHeight: '300px',
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 'calc(100vh - 32px)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="min-w-0 flex-1 pr-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white break-words leading-tight">
                  {sparePart.Ad}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-mono mt-1">
                  {sparePart.Kod}
                </p>
              </div>
              <AnimatedCloseButton
                onClick={() => onClose()}
                size="sm"
                className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              />
            </div>

            {/* Görsel Alanı */}
            <div className="flex-1 flex items-center justify-center p-8 bg-gray-50 dark:bg-gray-900 overflow-hidden">
              <img
                src={getFullImageUrl(sparePart.Gorsel)}
                alt={sparePart.Ad}
                loading="lazy"
                className="max-w-full max-h-full object-contain rounded"
                onLoad={handleImageLoad}
              />
            </div>

            {/* Footer */}
            {sparePart.GrupAdi && (
              <div className="flex-shrink-0 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Grup:</span> {sparePart.GrupAdi}
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SparePartImageModal;